import React, { memo, Fragment } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

import { COUNTRY_CODE_PHONE, ERROR_INPUT_TYPE } from '../constants';
import { classNames } from '../utils';

export const FormCard = memo(({ children, title, center = false }) => {
  return (
    <div className={`rounded-3xl w-full bg-white shadow`}>
      <div className="bg-blue-900 py-2 px-5 rounded-t-3xl">
        <h3 className={classNames("font-bold text-white", center ? 'text-center' : '')}>{title}</h3>
      </div>
      <div className="px-6 py-5 space-y-4">{children}</div>
    </div>
  );
})

/**
 * INPUT COMPONENT
 * @param {{ label: string, type: 'text' | 'password' | 'url' | 'number' | 'date' | 'tel' | 'select', name: string, register: Register, rules: Rules, errorType?: string, options?: {label: string, value: string}[] icon: React.Component, rest: import('react').HTMLInputTypeAttribute[]}} props
 */
export const FormCardInput = memo(({ label, type, name, register, rules, errorType, options, icon, ...rest }) => {
  return (
    <div className="w-full space-y-0.5">
      {label && <label htmlFor={name} className="text-blue-900 tracking-wider text-xs font-medium ml-3 flex">
        {label}
      </label>}
      <div className="bg-white rounded-full border border-gray-400 flex items-center relative">
        {
          type === 'select' ?
            <select
              title={document.getElementById(name)?.value}
              id={name}
              className="select font-medium rounded-full w-full text-gray-500 pl-5 pr-8 py-2 outline-none ring-0 focus:ring-2 focus:ring-general-teal transition-all disabled:cursor-not-allowed placeholder:text-sm placeholder:tracking-wide"
              {...register(name, rules || { required: true })}
              {...rest}
            >
              <>
                <option value="">Selectionner une valeur...</option>
                {
                  options.map((option, index) => {
                    if (typeof option === "string") return <option key={index} value={option}>{option}</option>
                    return <option key={index} value={option.value}>{option.label}</option>
                  })
                }
              </>
            </select>
            :
            <input
              title={name !== "title" && type !== 'url' ? document.getElementById(name)?.value : ""}
              type={type || "text"}
              id={name}
              className="rounded-full w-full text-gray-500 pl-5 pr-8 py-2 outline-none ring-0 focus:ring-2 focus:ring-general-teal transition-all disabled:cursor-not-allowed placeholder:text-sm placeholder:tracking-wide"
              {...register(name, rules || { required: true })}
              {...rest}
            />}

        <small className="mr-2 flex-shrink absolute right-1">{icon || null}</small>
      </div>
      {
        errorType && <p className="text-xs text-general-pink ml-3 flex space-x-1">
          <ExclamationCircleIcon className="h-4" />
          <span>{errorType !== 'required' ? ERROR_INPUT_TYPE[errorType]?.[name] || 'Veuillez fournir les informations correctes SVP.' : ERROR_INPUT_TYPE[errorType]}</span>
        </p>
      }
    </div>
  );
})

/**
 * SELECT LIST COUNTRY COMPONENT
 * @param {{ selected: {name: string, dial_code: string, flag: string, code: string}, setSelected: () => void }} props 
 * @returns {JSX.Element}
 */
export default function SelectListCountry({ selected, setSelected }) {
  return (
    <Listbox value={selected} onChange={setSelected}>
      <div className="relative mt-1">
        <Listbox.Button className="rounded-full text-gray-500 pl-5 pr-8 py-2 outline-none ring-0 focus:ring-2 focus:ring-general-teal transition-all disabled:cursor-not-allowed placeholder:text-sm placeholder:tracking-wide w-28 border border-gray-400">
          <span className="flex items-center space-x-1 truncate">
            <img src={`https://flagcdn.com/16x12/${selected.code.toLowerCase()}.webp`} width="16"
              height="12" alt={selected.name} /> <span>{selected.dial_code}</span>
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {COUNTRY_CODE_PHONE.map((country, index) => (
              <Listbox.Option
                key={index}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-8 ${active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                  }`
                }
                value={country}
              >
                {({ selected }) => (
                  <>
                    <span
                      title={country.name}
                      className={`flex items-center space-x-1 truncate ${selected ? 'font-medium' : 'font-normal'
                        }`}
                    >
                      <img src={`https://flagcdn.com/16x12/${country.code.toLowerCase()}.webp`} width="16"
                        height="12" alt={country.name} /> <span>{country.dial_code}</span>
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-amber-600">
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}

